import type { PageHelp } from '../../help/types';

/** Guide for `/settings/publish-targets` — every target of every project (spec §8.4). */
export const publishTargetsHelp: PageHelp = {
  title: 'Publish targets',
  intro:
    'A publish target says: publish THIS project to the environment this Glossa serves. You pick only the project. The environment and the storage location follow from the Glossa you are in: development publishes for the apps’ development, staging for staging, production for production. The same targets appear on each project’s settings page — this screen shows all of them together.',
  sections: ['The list', 'Adding a target'],
  contexts: {
    'target-dialog': 'Click “Add target” at the top right.',
  },
  tips: [
    'The files of a project are always at `locales/<project>/<locale>.json` in this Glossa’s own storage account. That URL, without the file name, is what the application puts in `VITE_I18N_BASE_URL`.',
    'A package has no target of its own: its keys reach the applications inside the bundles of the projects that subscribe to it.',
    'Removing a target stops publishing to it; its releases and the files already in storage stay where they are.',
  ],
  steps: [
    // ── The list ────────────────────────────────────────────────────────────────
    {
      selector: '[data-help="publish-targets-list"]',
      title: 'Read the targets',
      body: 'One row per target: which project is published, to which environment, and the exact URL the files are written to.',
      section: 'The list',
      i18n: {
        'de-AT': {
          title: 'Die Ziele lesen',
          body: 'Eine Zeile pro Ziel: welches Projekt veröffentlicht wird, in welche Umgebung, und die genaue URL, unter der die Dateien gespeichert werden.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje ciljeva',
          body: 'Jedan red po cilju: koji projekat se objavljuje, u koje okruženje i tačan URL na koji se datoteke upisuju.',
        },
      },
    },
    {
      selector: '[data-help="publish-targets-owner"]',
      title: 'See what is published',
      body: 'The project this target publishes: its whole bundle, including the keys of the packages it subscribes to and the keys it links from other projects.',
      section: 'The list',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Sehen, was veröffentlicht wird',
          body: 'Das Projekt, das dieses Ziel veröffentlicht: sein ganzes Bundle, einschließlich der Schlüssel abonnierter Pakete und der aus anderen Projekten verknüpften Schlüssel.',
        },
        'sr-Latn-RS': {
          title: 'Šta se objavljuje',
          body: 'Projekat koji ovaj cilj objavljuje: ceo njegov bundle, uključujući ključeve paketa na koje je pretplaćen i ključeve koje povezuje iz drugih projekata.',
        },
      },
    },
    {
      selector: '[data-help="publish-targets-path"]',
      title: 'Read the URL',
      body: 'The address of the project’s folder in this Glossa’s storage, then `{locale}.json` — the URL an application reads, one file per locale.',
      section: 'The list',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die URL lesen',
          body: 'Die Adresse des Projektordners im Storage dieses Glossa, dann `{locale}.json` — die URL, die eine Anwendung liest, eine Datei pro Sprache.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje URL-a',
          body: 'Adresa foldera projekta u storage-u ovog Glossa, pa `{locale}.json` — URL koji aplikacija čita, jedna datoteka po jeziku.',
        },
      },
    },
    {
      selector: '[data-help="publish-targets-auto-publish"]',
      title: 'Switch auto-publish',
      body: 'On: Glossa publishes this target by itself once the content has changed and the debounce has passed without a further change. Usually on for development, off for production.',
      section: 'The list',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Auto-Publish schalten',
          body: 'An: Glossa veröffentlicht dieses Ziel selbst, sobald sich der Inhalt geändert hat und die Wartezeit ohne weitere Änderung verstrichen ist. Meist an für development, aus für production.',
        },
        'sr-Latn-RS': {
          title: 'Uključivanje automatskog objavljivanja',
          body: 'Uključeno: Glossa sama objavljuje ovaj cilj čim se sadržaj promeni i prođe vreme čekanja bez nove izmene. Obično uključeno za development, isključeno za production.',
        },
      },
    },
    {
      selector: '[data-help="publish-targets-reviewed"]',
      title: 'Require reviewed translations',
      body: 'On: a publish to this target is refused while a value in the locales being published is still draft or machine. Only an administrator can force it past this gate.',
      section: 'The list',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Geprüfte Übersetzungen verlangen',
          body: 'An: Ein Publish auf dieses Ziel wird abgelehnt, solange ein Wert in den zu veröffentlichenden Sprachen noch draft oder machine ist. Nur ein Administrator kann ihn an dieser Sperre vorbei erzwingen.',
        },
        'sr-Latn-RS': {
          title: 'Zahtevanje pregledanih prevoda',
          body: 'Uključeno: objavljivanje na ovaj cilj se odbija dok je neka vrednost na jezicima koji se objavljuju još u statusu draft ili machine. Samo administrator može da ga nametne mimo ovog uslova.',
        },
      },
    },
    {
      selector: '[data-help="publish-targets-qa-clean"]',
      title: 'Require QA-clean translations',
      body: 'On: a publish is refused while any value has a QA error, such as placeholders that do not match the source. QA warnings do not block.',
      section: 'The list',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'QA-saubere Übersetzungen verlangen',
          body: 'An: Ein Publish wird abgelehnt, solange ein Wert einen QA-Fehler hat, etwa Platzhalter, die nicht zur Ausgangssprache passen. QA-Warnungen blockieren nicht.',
        },
        'sr-Latn-RS': {
          title: 'Zahtevanje QA-ispravnih prevoda',
          body: 'Uključeno: objavljivanje se odbija dok neka vrednost ima QA grešku, na primer placeholder-e koji se ne poklapaju sa izvornim tekstom. QA upozorenja ne blokiraju.',
        },
      },
    },
    {
      selector: '[data-help="publish-targets-releases"]',
      title: 'Open the releases',
      body: 'Goes to the Releases page of that project, where it is published, verified and rolled back.',
      section: 'The list',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die Releases öffnen',
          body: 'Öffnet die Seite „Releases“ dieses Projekts, wo veröffentlicht, geprüft und zurückgesetzt wird.',
        },
        'sr-Latn-RS': {
          title: 'Otvaranje izdanja',
          body: 'Otvara stranicu „Releases“ tog projekta, gde se objavljuje, proverava i vraća na ranije izdanje.',
        },
      },
    },
    {
      selector: '[data-help="publish-targets-remove"]',
      title: 'Remove a target',
      body: 'After you confirm, publishing to this target stops. Its releases and the files already in storage stay, so an application reading them keeps working.',
      section: 'The list',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Ein Ziel entfernen',
          body: 'Nach der Bestätigung wird nicht mehr auf dieses Ziel veröffentlicht. Seine Releases und die bereits gespeicherten Dateien bleiben, eine Anwendung, die sie liest, funktioniert also weiter.',
        },
        'sr-Latn-RS': {
          title: 'Uklanjanje cilja',
          body: 'Posle potvrde objavljivanje na ovaj cilj prestaje. Njegova izdanja i datoteke koje su već u storage-u ostaju, pa aplikacija koja ih čita nastavlja da radi.',
        },
      },
    },

    // ── Adding a target ─────────────────────────────────────────────────────────
    {
      selector: '[data-help="publish-targets-add"]',
      title: 'Add a target',
      body: 'Opens the dialog: choose the project, and the environment and the URL are filled in.',
      section: 'Adding a target',
      i18n: {
        'de-AT': {
          title: 'Ein Ziel hinzufügen',
          body: 'Öffnet den Dialog: Wählen Sie das Projekt — Umgebung und URL werden ergänzt.',
        },
        'sr-Latn-RS': {
          title: 'Dodavanje cilja',
          body: 'Otvara dijalog: izaberite projekat, a okruženje i URL se sami popunjavaju.',
        },
      },
    },
    {
      selector: '[data-help="target-new-owner"]',
      title: 'Choose the project',
      body: 'The project to publish — to release hbh, choose hbh. A package is not in the list: its keys go out with the projects that subscribe to it.',
      section: 'Adding a target',
      context: 'target-dialog',
      i18n: {
        'de-AT': {
          title: 'Das Projekt wählen',
          body: 'Das zu veröffentlichende Projekt — für ein Release von hbh wählen Sie hbh. Pakete stehen nicht in der Liste: Ihre Schlüssel werden mit den Projekten veröffentlicht, die sie abonnieren.',
        },
        'sr-Latn-RS': {
          title: 'Izbor projekta',
          body: 'Projekat koji se objavljuje — za izdanje hbh izaberite hbh. Paketi nisu u listi: njihovi ključevi izlaze zajedno sa projektima koji su na njih pretplaćeni.',
        },
      },
    },
    {
      selector: '[data-help="target-new-path"]',
      title: 'Check where the files go',
      body: 'The environment this Glossa serves and the URL the files will have: `locales/<project>/` in this Glossa’s storage account. A project has one target per environment, so a project that already has one cannot get a second.',
      section: 'Adding a target',
      context: 'target-dialog',
      i18n: {
        'de-AT': {
          title: 'Prüfen, wohin die Dateien gehen',
          body: 'Die Umgebung, die dieses Glossa bedient, und die URL der Dateien: `locales/<Projekt>/` im Storage-Account dieses Glossa. Ein Projekt hat ein Ziel pro Umgebung; hat es schon eines, kann kein zweites angelegt werden.',
        },
        'sr-Latn-RS': {
          title: 'Provera odredišta datoteka',
          body: 'Okruženje koje ovaj Glossa opslužuje i URL datoteka: `locales/<projekat>/` u storage account-u ovog Glossa. Projekat ima jedan cilj po okruženju, pa projekat koji ga već ima ne može dobiti drugi.',
        },
      },
    },
    {
      selector: '[data-help="target-new-reviewed"]',
      title: 'Require reviewed translations',
      body: 'The same gate as the “Reviewed only” switch in the list; it can be changed later.',
      section: 'Adding a target',
      context: 'target-dialog',
      i18n: {
        'de-AT': {
          title: 'Geprüfte Übersetzungen verlangen',
          body: 'Dieselbe Sperre wie der Schalter „Reviewed only“ in der Liste; sie lässt sich später ändern.',
        },
        'sr-Latn-RS': {
          title: 'Zahtevanje pregledanih prevoda',
          body: 'Isti uslov kao prekidač „Reviewed only“ u listi; može se kasnije promeniti.',
        },
      },
    },
    {
      selector: '[data-help="target-new-auto-publish"]',
      title: 'Publish automatically',
      body: 'The same as the “Auto-publish” switch in the list; a new target waits 30 seconds after the last change before it publishes itself.',
      section: 'Adding a target',
      context: 'target-dialog',
      i18n: {
        'de-AT': {
          title: 'Automatisch veröffentlichen',
          body: 'Entspricht dem Schalter „Auto-publish“ in der Liste; ein neues Ziel wartet nach der letzten Änderung 30 Sekunden, bevor es sich selbst veröffentlicht.',
        },
        'sr-Latn-RS': {
          title: 'Automatsko objavljivanje',
          body: 'Isto kao prekidač „Auto-publish“ u listi; novi cilj čeka 30 sekundi posle poslednje izmene pre nego što se sam objavi.',
        },
      },
    },
    {
      selector: '[data-help="target-new-create"]',
      title: 'Create the target',
      body: 'Saves the target and adds it to the list. It stays disabled until a project is chosen, and for a project that already has its target in this environment.',
      section: 'Adding a target',
      context: 'target-dialog',
      i18n: {
        'de-AT': {
          title: 'Das Ziel anlegen',
          body: 'Speichert das Ziel und fügt es der Liste hinzu. Gesperrt, bis ein Projekt gewählt ist, und für ein Projekt, das sein Ziel in dieser Umgebung schon hat.',
        },
        'sr-Latn-RS': {
          title: 'Kreiranje cilja',
          body: 'Čuva cilj i dodaje ga u listu. Zaključano je dok se ne izabere projekat, kao i za projekat koji već ima svoj cilj u ovom okruženju.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Publish-Ziele',
      intro:
        'Ein Publish-Ziel sagt: Veröffentliche DIESES Projekt in die Umgebung, die dieses Glossa bedient. Sie wählen nur das Projekt. Umgebung und Speicherort ergeben sich aus dem Glossa, in dem Sie arbeiten: development veröffentlicht für die development-Umgebung der Anwendungen, staging für staging, production für production. Dieselben Ziele stehen auf der Einstellungsseite jedes Projekts — hier sind sie alle zusammen.',
      sections: {
        'The list': 'Die Liste',
        'Adding a target': 'Ein Ziel hinzufügen',
      },
      contexts: {
        'target-dialog': 'Klicken Sie rechts oben auf „Add target“.',
      },
      tips: [
        'Die Dateien eines Projekts liegen immer unter `locales/<Projekt>/<Sprache>.json` im eigenen Storage-Account dieses Glossa. Diese URL ohne Dateinamen trägt die Anwendung in `VITE_I18N_BASE_URL` ein.',
        'Ein Paket hat kein eigenes Ziel: Seine Schlüssel erreichen die Anwendungen in den Bundles der Projekte, die es abonnieren.',
        'Ein entferntes Ziel wird nicht mehr veröffentlicht; seine Releases und die bereits gespeicherten Dateien bleiben.',
      ],
    },
    'sr-Latn-RS': {
      title: 'Ciljevi za objavljivanje',
      intro:
        'Cilj za objavljivanje kaže: objavi OVAJ projekat u okruženje koje ovaj Glossa opslužuje. Birate samo projekat. Okruženje i lokacija slede iz Glossa u kome radite: development objavljuje za development okruženje aplikacija, staging za staging, production za production. Isti ciljevi se vide i na stranici podešavanja svakog projekta — ovde su svi zajedno.',
      sections: {
        'The list': 'Lista',
        'Adding a target': 'Dodavanje cilja',
      },
      contexts: {
        'target-dialog': 'Kliknite na „Add target“ u gornjem desnom uglu.',
      },
      tips: [
        'Datoteke projekta su uvek na `locales/<projekat>/<jezik>.json` u sopstvenom storage account-u ovog Glossa. Taj URL bez imena datoteke aplikacija upisuje u `VITE_I18N_BASE_URL`.',
        'Paket nema svoj cilj: njegovi ključevi stižu do aplikacija u bundle-ovima projekata koji su na njega pretplaćeni.',
        'Uklonjen cilj se više ne objavljuje; njegova izdanja i datoteke koje su već u storage-u ostaju.',
      ],
    },
  },
};
